import { Controller } from "@hotwired/stimulus"
import { Map, TileLayer, CircleMarker, LatLngBounds } from "leaflet"
import "leaflet/dist/leaflet.css"

const STATUS_COLORS = {
  operational: "#16a34a",
  maintenance: "#ca8a04",
  inactive: "#6b7280",
  planned: "#2563eb",
}

export default class extends Controller {
  static targets = ["map", "count", "search", "region", "siteType", "from", "to"]
  static values = { sites: Array }

  connect() {
    this._initMap()
    this._renderMarkers(this.sitesValue)
  }

  disconnect() {
    this._map?.remove()
    this._map = null
  }

  filter() {
    const search   = (this.hasSearchTarget  ? this.searchTarget.value   : "").toLowerCase().trim()
    const region   = this.hasRegionTarget   ? this.regionTarget.value   : ""
    const siteType = this.hasSiteTypeTarget ? this.siteTypeTarget.value : ""
    const from     = this.hasFromTarget     ? this.fromTarget.value     : ""
    const to       = this.hasToTarget       ? this.toTarget.value       : ""

    const filtered = this.sitesValue.filter(site => {
      if (search && !site.name.toLowerCase().includes(search) && !site.state_region.toLowerCase().includes(search)) return false
      if (region   && site.state_region !== region)   return false
      if (siteType && site.site_type    !== siteType) return false
      if (from && site.commissioned_at < from) return false
      if (to   && site.commissioned_at > to)   return false
      return true
    })

    this._renderMarkers(filtered)
  }

  // private

  _initMap() {
    this._map = new Map(this.mapTarget, { center: [5, 20], zoom: 3 })
    new TileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(this._map)
    this._markers = []
  }

  _renderMarkers(sites) {
    this._markers.forEach(m => m.remove())
    this._markers = []

    if (this.hasCountTarget) this.countTarget.textContent = sites.length

    const points = []
    sites.forEach(site => {
      if (site.lat == null || site.lng == null) return

      const color = STATUS_COLORS[site.status] || "#6b7280"
      const marker = new CircleMarker([site.lat, site.lng], {
        radius: 8,
        fillColor: color,
        color: "#fff",
        weight: 2,
        opacity: 1,
        fillOpacity: 0.85,
      })

      marker.bindPopup(this._popup(site))
      marker.addTo(this._map)
      this._markers.push(marker)
      points.push([site.lat, site.lng])
    })

    if (points.length > 0) {
      try {
        this._map.fitBounds(new LatLngBounds(points), { padding: [40, 40], maxZoom: 10 })
      } catch (_) { /* ignore if map already destroyed */ }
    }
  }

  _popup(site) {
    return `
      <div style="min-width:180px;font-family:inherit">
        <p style="font-weight:600;margin:0 0 4px">${site.name}</p>
        <p style="margin:0;color:#6b7280;font-size:0.85em">${site.state_region}</p>
        <p style="margin:2px 0;font-size:0.85em">${site.site_type_label}</p>
        <p style="margin:0 0 6px;font-size:0.85em">${site.status_label}</p>
        <a href="${site.url}" style="color:#00558A;font-size:0.85em;font-weight:500">
          View site →
        </a>
      </div>
    `
  }
}
